import { IntegrationCreatePanelView } from '../view/IntegrationCreatePanelView';
import { templateIntegrationCreatePanel } from '../view/template';
import { BaseWebComponentController, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { IntegrationCreatePanelWebComponent } from '../component/IntegrationCreatePanelWebComponent';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { CreateIntegrationEvent } from '../types/CreateIntegrationEvent';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { GzToast, ToastSeverity } from '../../../ui/gzToastNotification/type';
import { getRequestDefaultOptions } from '../../../utils/request.utils';
import { EditSensorEvent } from '../../editIntegration/types/events';
import {
  INTEGRATIONS_WITHOUT_CONNECTIVITY,
  INTEGRATIONS_WITHOUT_CONNECTIVITY_BUT_WITH_SETUP,
  IntegrationType
} from '../../types/integration-type';
import { WEB_COMPONENT_INTEGRATION_EDIT_INTEGRATION } from '../../../webcomponents.constants';
import { License } from '../../integrationsGrid/types';
import { htmlEncode } from '../../../utils/escape.utils';
import { GzDialog } from '../../../ui/gzDialog/component/GzDialog';
import { IntegrationInformationWebComponent } from '../../integrationInformation';

export class IntegrationCreatePanelController extends BaseWebComponentController {
  public component: IntegrationCreatePanelWebComponent;
  public view: IntegrationCreatePanelView;
  public viewConstructor: typeof IntegrationCreatePanelView;
  private currentStep: number;

  constructor(component: IntegrationCreatePanelWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: IntegrationCreatePanelView,
      viewTemplate: templateIntegrationCreatePanel,
    });
  }

  public async getDataForView(): Promise<any> {
    this.component.licenses = await this.getCompanyLicenses();
    return {
      licenses: this.component.licenses,
    };
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$wrapper,
        event: CreateIntegrationEvent.OnViewInit,
        callback: this.initFirstStep,
      },
      {
        element: this.view.dom.$integrationSelectCompany,
        event: CreateIntegrationEvent.OnCompanySelect,
        callback: this.refreshIntegrationsData,
      },
      {
        element: this.view.dom.$cancelButton,
        event: 'click',
        callback: this.showCancelWarningDialog,
      },
      {
        element: this.view.dom.$closeButton,
        event: 'click',
        callback: this.closeIntegrationWindow,
      },
      {
        element: this.view.dom.$addNewButton,
        event: 'click',
        callback: this.initFirstStep,
      },
      {
        element: document.body,
        event: CreateIntegrationEvent.OnLicenseChanged,
        callback: this.showSelectIntegrationType,
      },
      {
        element: this.view.dom.$wrapper,
        event: CreateIntegrationEvent.OnShowRequirements,
        callback: this.showRequirements,
      },
      {
        element: this.view.dom.$wrapper,
        event: CreateIntegrationEvent.OnShowInformationAboutIntegration,
        callback: this.showNonConfigurableIntegrationPopup,
      },
      {
        element: this.view.dom.$wrapper,
        event: CreateIntegrationEvent.OnValidDataCompletion,
        callback: this.enableSave,
      },
      {
        element: this.view.dom.$wrapper,
        event: CreateIntegrationEvent.OnInValidDataCompletion,
        callback: this.disableSave,
      },
      {
        element: this.view.dom.$editIntegration,
        event: EditSensorEvent.change,
        callback: this.editSensorDataChange,
      },
      {
        element: this.view.dom.$editIntegration,
        event: EditSensorEvent.isFormValid,
        callback: this.shouldEnableSave,
      },
      {
        element: this.view.dom.$confirmButton,
        event: 'click',
        callback: this.confirmRequirements,
      },
      {
        element: this.view.dom.$applyButton,
        event: 'click',
        callback: this.saveIntegration,
      },
      {
        element: this.view.dom.$nextButton,
        event: 'click',
        callback: this.nextHandler,
      },
      {
        element: this.view.dom.$backButton,
        event: 'click',
        callback: this.backHandler,
      },
      {
        element: document.body,
        event: CreateIntegrationEvent.OnLicenseAdd,
        callback: this.showCompanyLicenseTab,
      },
      {
        element: document.body,
        event: CreateIntegrationEvent.OnShowTechnicalRequest,
        callback: this.showTechnicalSupportRequestForm,
      },
    ];
  }

  private async nextHandler(event: WebComponentEvent) {
    switch (this.currentStep) {
      case this.view.allowedSteps.SelectIntegrationCompany:
        await this.showSelectIntegrationType(event);
        break;
      case this.view.allowedSteps.ShowRequirements:
        this.showAddIntegration(event);
        break;
    }
  }

  private confirmRequirements() {
    if (this.view.dom.$requirements.getIntegrationType() === IntegrationType.NSVA) {
      this.closeIntegrationWindow();
      return;
    }
    this.switchStep(this.view.allowedSteps.EditIntegration);
  }

  private async backHandler(event: WebComponentEvent) {
    switch (this.currentStep) {
      case this.view.allowedSteps.SelectIntegrationType:
        this.showSelectCompany(event);
        break;
      case this.view.allowedSteps.AddIntegration:
        this.showRequirements(event);
        break;
      case this.view.allowedSteps.ShowRequirements:
        await this.showSelectIntegrationType(event);
        this.component.dispatchEvent(
          new CustomEvent(CreateIntegrationEvent.OnModifyDialogTitle, {
            detail: {
              title: htmlEncode(this.translator.getSensorSetupText()),
            },
            bubbles: true,
            composed: true,
          })
        );
        break;
    }
  }

  private initFirstStep(event: WebComponentEvent) {
    this.component.dispatchEvent(
      new CustomEvent(CreateIntegrationEvent.OnModifyDialogTitle, {
        detail: {
          title: htmlEncode(this.translator.getSensorSetupText()),
        },
        bubbles: true,
        composed: true,
      })
    );

    if (this.dependencies.isPartnerCompany()) {
      this.showSelectCompany(event);
    } else {
      this.showSelectIntegrationType(event).then();
    }
  }

  private showSelectCompany(event: WebComponentEvent) {
    this.switchStep(this.view.allowedSteps.SelectIntegrationCompany, event);
  }

  private async showSelectIntegrationType(event: WebComponentEvent) {
    await this.view.dom.$integrationTypeList.controller.render();
    this.switchStep(this.view.allowedSteps.SelectIntegrationType, event);
  }

  public addNewIntegration() {
    this.view.dom.$applyButton.innerText = this.dependencies.translateFn('Add sensor');
    this.view.dom.$editIntegration.controller.testConnectivitySuccessMessage = this.dependencies.translateFn(
      'Connection successful. You can add the sensor.'
    );
    this.disableSave();
    if (this.dependencies.isPartnerCompany()) {
      this.component.dispatchEvent(
        new CustomEvent(CreateIntegrationEvent.OnSwitchToSelectType, {
          detail: {},
          bubbles: true,
          composed: true,
        })
      );
      this.view.dom.$integrationSelectCompany.dispatchEvent(
        new CustomEvent(CreateIntegrationEvent.CreateCompanySelector, {
          detail: {},
          bubbles: true,
          composed: true,
        })
      );
      this.switchStep(this.view.allowedSteps.SelectIntegrationCompany);
    } else {
      this.switchStep(this.view.allowedSteps.SelectIntegrationType);
    }
    this.view.dom.$editIntegration.initForm().catch(console.error);
  }

  public async editIntegration(integration) {
    this.view.dom.$applyButton.innerText = this.dependencies.translateFn('Apply');
    this.view.dom.$editIntegration.controller.testConnectivitySuccessMessage = this.dependencies.translateFn(
      'Connection successful. You can apply the changes.'
    );
    this.view.dom.$editIntegration.injectDependencies(this.dependencies);
    await this.view.dom.$editIntegration.setupEditIntegration(integration);
    this.switchStep(this.view.allowedSteps.EditIntegration);
  }

  private editSensorDataChange(): void {
    if (INTEGRATIONS_WITHOUT_CONNECTIVITY.includes(this.view.dom.$editIntegration.getIntegrationType())
     || INTEGRATIONS_WITHOUT_CONNECTIVITY_BUT_WITH_SETUP.includes(this.view.dom.$editIntegration.getIntegrationType())) {
      return;
    }

    this.disableSave();
  }

  private showSuccessView(
    integrationName: string,
    integrationType: IntegrationType,
    sensors: Object[],
    event: WebComponentEvent
  ) {
    if (!this.view.isConnected) {
      return;
    }
    this.view.dom.$successWindow.updateBasedOnSensors(integrationName, integrationType, sensors);

    this.switchStep(this.view.allowedSteps.SuccessView, event);
  }

  private switchStepSelectIntegrationCompany() {
    toggleVisibility(this.view.dom.$integrationSelectCompany, true);
    toggleVisibility(this.view.dom.$nextButton, true);
    toggleVisibility(this.view.dom.$cancelButton, true);
    toggleVisibility(this.view.dom.$wizard, true);
  }

  private switchStepSelectIntegrationType() {
    toggleVisibility(this.view.dom.$integrationTypeList, true);
    toggleVisibility(this.view.dom.$cancelButton, true);
    toggleVisibility(this.view.dom.$backButton, this.dependencies.isPartnerCompany());
    toggleVisibility(this.view.dom.$wizard, true);
  }

  private switchStepShowRequirements() {
    toggleVisibility(this.view.dom.$nextButton, true);
    toggleVisibility(this.view.dom.$requirements, true);
    toggleVisibility(this.view.dom.$cancelButton, true);
    toggleVisibility(this.view.dom.$backButton, true);
    toggleVisibility(this.view.dom.$wizard, true);
  }

  private switchStepAddIntegration() {
    toggleVisibility(this.view.dom.$editIntegration, true);
    toggleVisibility(this.view.dom.$applyButton, true);
    toggleVisibility(this.view.dom.$cancelButton, true);
    toggleVisibility(this.view.dom.$backButton, true);
    toggleVisibility(this.view.dom.$wizard, true);
    toggleVisibility(
      this.view.dom.$editIntegration.controller.view.dom.$companyName,
      this.dependencies.isPartnerCompany()
    );
  }

  private switchStepSuccessView() {
    toggleVisibility(this.view.dom.$successWindow, true);
    toggleVisibility(this.view.dom.$closeButton, true);
    toggleVisibility(this.view.dom.$addNewButton, true);
    toggleVisibility(this.view.dom.$wizard, true);
  }

  private switchStepEditIntegration() {
    toggleVisibility(this.view.dom.$editIntegration, true);
    toggleVisibility(this.view.dom.$applyButton, true);
    toggleVisibility(this.view.dom.$cancelButton, true);
    toggleVisibility(this.view.dom.$editIntegration.controller.view.dom.$confirmedRequirementsButton, true);
  }

  private switchStepShowRequirementsOnEdit() {
    toggleVisibility(this.view.dom.$requirements, true);
    toggleVisibility(this.view.dom.$cancelButton, true);
    toggleVisibility(this.view.dom.$confirmButton, true);
  }

  private hideAllElements() {
    toggleVisibility(this.view.dom.$integrationSelectCompany, false);
    toggleVisibility(this.view.dom.$nextButton, false);
    toggleVisibility(this.view.dom.$integrationTypeList, false);
    toggleVisibility(this.view.dom.$editIntegration, false);
    toggleVisibility(this.view.dom.$applyButton, false);
    toggleVisibility(this.view.dom.$requirements, false);
    toggleVisibility(this.view.dom.$successWindow, false);
    toggleVisibility(this.view.dom.$closeButton, false);
    toggleVisibility(this.view.dom.$addNewButton, false);
    toggleVisibility(this.view.dom.$cancelButton, false);
    toggleVisibility(this.view.dom.$confirmButton, false);
    toggleVisibility(this.view.dom.$backButton, false);
    toggleVisibility(this.view.dom.$wizard, false);
    toggleVisibility(this.view.dom.$editIntegration.controller.view.dom.$companyName, false);
    toggleVisibility(this.view.dom.$editIntegration.controller.view.dom.$confirmedRequirementsButton, false);
  }

  private switchStep(step, event?: WebComponentEvent) {
    this.currentStep = step;
    if (
      ![this.view.allowedSteps.EditIntegration, this.view.allowedSteps.ShowRequirementsOnEdit].includes(step) &&
      event
    ) {
      event.target.setAttribute('data-step', step.toString());
      this.view.dom.$wizard.updateStep(event);
    }
    this.hideAllElements();
    switch (step) {
      case this.view.allowedSteps.SelectIntegrationCompany:
        this.switchStepSelectIntegrationCompany();
        break;

      case this.view.allowedSteps.SelectIntegrationType:
        this.switchStepSelectIntegrationType();
        break;

      case this.view.allowedSteps.ShowRequirements:
        this.switchStepShowRequirements();
        break;

      case this.view.allowedSteps.AddIntegration:
        this.switchStepAddIntegration();
        break;

      case this.view.allowedSteps.SuccessView:
        this.switchStepSuccessView();
        break;

      case this.view.allowedSteps.EditIntegration:
        this.switchStepEditIntegration();
        break;

      case this.view.allowedSteps.ShowRequirementsOnEdit:
        this.switchStepShowRequirementsOnEdit();
        break;
    }
  }

  private showAddIntegration(event: WebComponentEvent) {
    this.view.dom.$editIntegration.controller.view.dom.$companyName.innerText =
      this.dependencies.translateFn('Company: ') + this.dependencies.getActiveCompany().companyName;

    this.view.dom.$editIntegration.injectDependencies(this.dependencies);
    this.view.dom.$editIntegration.setIntegrationType(this.view.dom.$requirements.getIntegrationType());
    this.switchStep(this.view.allowedSteps.AddIntegration, event);
    this.disableSave();
    this.view.dom.$editIntegration.initForm().catch(console.error);
  }

  private showRequirements(event: WebComponentEvent) {
    this.view.dom.$nextButton.disabled = false;
    if (event.detail.type) {
      this.view.dom.$requirements.setIntegrationType(event.detail.type);
      this.view.dom.$requirements.setNotConfigurableByUser(event.detail.nonUserConfigurable);
      this.component.dispatchEvent(
        new CustomEvent(CreateIntegrationEvent.OnModifyDialogTitle, {
          detail: {
            title: !event.detail.isEditPanel
              ? htmlEncode(this.translator.getSensorSetupText(event.detail.type))
              : htmlEncode(
                  this.dependencies.translateFn('Edit sensor - {integrationName}', {
                    integrationName: this.translator.getTranslatedIntegrationNameByType(event.detail.type),
                  })
                ),
          },
          bubbles: true,
          composed: true,
        })
      );
    }
    if (event.target.matches(WEB_COMPONENT_INTEGRATION_EDIT_INTEGRATION)) {
      this.switchStep(this.view.allowedSteps.ShowRequirementsOnEdit);
    } else {
      this.switchStep(this.view.allowedSteps.ShowRequirements, event);
    }
  }

  private async showNonConfigurableIntegrationPopup(event: WebComponentEvent) {
    const integrationInformation = new IntegrationInformationWebComponent();
    integrationInformation.injectDependencies(this.dependencies);
    await integrationInformation.start();
    integrationInformation.setIntegrationType(event.detail.type);
    integrationInformation.setUserConfigurable(event.detail.nonUserConfigurable);
    const bodyContent = document.createElement('div');
    const translations = this.translator.getSensorsIntegrationsStrings();

    const footerContent = document.createElement('div');
    footerContent.innerHTML = `
        <gz-button id="button-back" type="quiet" color="secondary">
          ${this.dependencies.translateFn('Back')}
        </gz-button>

        <gz-button id="button-cancel" type="outline" color="secondary" >
          ${this.dependencies.translateFn('Cancel')}
        </gz-button>
    `;
    bodyContent.appendChild(integrationInformation);
    footerContent.querySelector('#button-back')?.addEventListener('click', () => {
      dialog.dismiss();
    });

    footerContent.querySelector('#button-cancel')?.addEventListener('click', () => {
      this.closeIntegrationWindow();
      dialog.dismiss();
    });

    const windowConfigs: GzDialog = {
      headline: this.translator.getSensorSetupText(event.detail.type),
      bodyContent: bodyContent,
      footerContent: footerContent,
      isOpen: true,
      closableOutsideBox: false,
      isWindow: true,
      overlayZIndex: 9999,
      onCloseHandler: () => {
        this.closeIntegrationWindow();
        dialog.dismiss();
      },
    };

    const dialog = GzDialogWebComponent.buildDialogElement(
      windowConfigs,
      document.querySelector('.mainContainer') as HTMLElement
    );
  }

  private shouldEnableSave(ev: CustomEvent): void {
    if (ev.detail.isFormValid) {
      this.enableSave();
      this.addRemoveTextToToolTip('');
      return;
    }
    this.addRemoveTextToToolTip(ev.detail.addSensorTooltipMessage);
    this.disableSave();
  }

  private addRemoveTextToToolTip(message: string): void {
      this.view.dom.$applyButtonTooltip.tooltipText = message;
  }

  private enableSave() {
    this.view.dom.$applyButton.disabled = false;
  }

  private refreshIntegrationsData() {
    this.component.dispatchEvent(
      new CustomEvent(CreateIntegrationEvent.OnSwitchToSelectType, {
        detail: {},
        bubbles: true,
        composed: true,
      })
    );
    this.view.dom.$nextButton.disabled = false;
  }

  private disableSave() {
    this.view.dom.$applyButton.disabled = true;
  }

  private async saveIntegration(event: WebComponentEvent) {
    this.view.dom.$applyButton.disabled = true;
    this.view.dom.$applyButton.showSpinner = true;

    try {
      const formParams = await this.view.dom.$editIntegration.getValue;
      const requestParams = {
        companyId: this.dependencies.getActiveCompany().companyId,
        integrationType: formParams['type'],
        integrationName: formParams['name'],
        sensors: formParams['sensors'],
        integrationSpecifics: formParams['integrationSpecifics'],
      };

      if (formParams._id) {
        requestParams['integrationId'] = formParams['_id'];
      }

      await this.dependencies.api.clients.SensorsIntegrations.saveIntegration(
        requestParams,
        getRequestDefaultOptions()
      );
      if (formParams._id) {
        this.showSuccessToast();
      } else {
        this.showSuccessView(formParams['name'], formParams['type'], formParams['sensors'], event);
      }
    } catch (e) {
      if (e.code && e.exceptionType === 'Exceptions\\SensorConnectivityException') {
        if (e?.message && !e?.message.endsWith('.')) {
          e.message = e.message + '.';
        }
        const errorDescription =
          !e['errorUri'] ? e?.message + ' ' : '';
        e.message =
          errorDescription +
          this.view.translateSensorConnectivityErrorCodes(e.code);
        this.showErrorToast(e);
        return;
      }

      this.showErrorToast(e);
    } finally {
      this.view.dom.$applyButton.disabled = false;
      this.view.dom.$applyButton.showSpinner = false;
    }
  }

  private showErrorToast(error: Error) {
    const errorMessage = error?.message || this.translator.getGenericErrorMessage();
    const toastConfig: GzToast = {
      message: errorMessage,
      severity: ToastSeverity.ERROR,
      keepOpen: true,
    };

    if (error['errorUri']) {
      toastConfig.hasAction = true;
      toastConfig.actionButtonText = this.dependencies.translateFn('View more');
      toastConfig.showInlineButton = true;
      toastConfig.actionCallback = () => {
        return window.open(error['errorUri'], '_blank');
      };
    }
    GzToastNotificationWebComponent.buildToastWrapperElement(toastConfig, undefined, false);
  }

  private showSuccessToast() {
    if (!this.view.isConnected) {
      return;
    }

    const message = this.dependencies.translateFn('Sensor has been edited.');
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: message,
      severity: ToastSeverity.SUCCESS,
    });
    this.closeIntegrationWindow();
  }

  private closeIntegrationWindow() {
    this.component.dispatchEvent(
      new CustomEvent(CreateIntegrationEvent.OnWindowCancel, {
        detail: {},
        bubbles: true,
        composed: true,
      })
    );
  }

  public showCancelWarningDialog() {
    if (
      ![this.view.allowedSteps.EditIntegration, this.view.allowedSteps.AddIntegration].includes(this.currentStep) ||
      !this.view.dom.$editIntegration.hasChangedData()
    ) {
      this.closeIntegrationWindow();
      return;
    }
    const windowBodyHtml = `
      <p>
        ${this.dependencies.translateFn('Changes you have made will be lost.')}
      </p>`;

    const windowFooterHtml = `<gz-button id='confirm' type='quiet' color='negative'>
       ${this.dependencies.translateFn('DISCARD')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='primary' class='mr-2'>
        ${this.dependencies.translateFn('KEEP CHANGES')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${this.dependencies.translateFn('Cancel')}
      </gz-button>`;

    const windowTitle = this.dependencies.translateFn('Discard changes?');
    const confirmCallback = () => {
      this.closeIntegrationWindow();
    };

    GzDialogWebComponent.computeCustomDialog(windowBodyHtml, windowFooterHtml, windowTitle, null, confirmCallback);
  }

  showCompanyLicenseTab(event: WebComponentEvent) {
    let companyId = this.dependencies.getActiveCompany().companyId;
    if (event.detail && event.detail.companyId) {
      companyId = event.detail.companyId;
    }
    this.dependencies.Ext.showCompanyLicenseTab(companyId);
  }

  showTechnicalSupportRequestForm() {
    this.dependencies.api.clients.GetTechnicalForm.showTechnicalForm();
  }

  async getCompanyLicenses(): Promise<Array<License>> {
    return this.dependencies.api.clients.Licenses.getCompanyLicenses(
      { companyId: this.dependencies.getActiveCompany().companyId },
      getRequestDefaultOptions()
    );
  }
}
